import React, { useEffect, useState, useCallback } from 'react'
import {
  Container,
  HeaderBar,
  DataGridTable,
  SubmitDialog,
  Toast as toast,
} from 'components'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import styles from './eldVisibility.module.scss'
import { eldListColumns, actionList } from './eldListTableData'
import strings from 'strings'
import { isViewOnly } from 'tools/auth'
import { getEldVisibilityList, deleteEldVisibility } from 'api'
import { IELD } from 'interfaces/IEldVisibility'
import { reportException } from 'tools/logs'
import AvailableElds from './availableElds'

const t = strings.ELD_VISIBILITY

const EldVisibilityList: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [btnLoader, setBtnLoader] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [selectedElds, setSelectedElds] = useState<number[]>([])
  const [eldList, setEldList] = useState<IELD[]>([])
  const [selectedRowData, setSelectedRowData] = useState<IELD | null>(null)
  const [availableEldsBladeOpen, setAvailableEldsBladeOpen] = useState(false)

  const fetchEldList = useCallback(() => {
    setLoading(true)
    getEldVisibilityList()
      .then((response) => {
        if (response.status === 200) {
          setEldList(response.data ?? [])
        }
      })
      .catch((error: Error) => {
        reportException(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    fetchEldList()
  }, [fetchEldList])

  const handleActions = (_id: number | string, actionName: string) => {
    const selectedEld = eldList.find((item) => item.id === _id)
    if (!selectedEld) return

    setSelectedRowData(selectedEld)
    if (actionName.toLowerCase() === 'remove') {
      setShowDeleteModal(true)
    }
  }

  const handleDelete = () => {
    const idsToDelete = selectedRowData 
      ? [selectedRowData?.id] 
      : eldList
          .filter(eld => selectedElds?.includes(eld?.id))
          .map(eld => eld?.id)

    if (idsToDelete?.length === 0) return

    setBtnLoader(true)
    deleteEldVisibility(idsToDelete)
      .then((response) => {
        if (response?.status === 200) {
          toast({
            id: 'deleteEldSuccess',
            type: 'success',
            subTitle: response?.message,
          })
          fetchEldList()
          setSelectedElds([])
        }
      })
      .catch((error: Error) => {
        reportException(error)
      })
      .finally(() => {
        setBtnLoader(false)
        setShowDeleteModal(false)
        setShowBulkDeleteModal(false)
        setSelectedRowData(null)
      })
  }

  const handleSelectionChange = (selection: GridRowSelectionModel) => {
    setSelectedElds(selection as number[])
  }

  return (
    <>
      <HeaderBar
        title={t.CONFIGURED_ELDS}
        {...(!isViewOnly() && {
          primaryBtnProps: {
            id: 'addEldProvider',
            'data-testid': 'add-eld-provider-btn',
            label: t.ADD_ELD_PROVIDERS,
            onClick: () => setAvailableEldsBladeOpen(true),
          },
          secondaryBtnProps: {
            id: 'bulkDeleteElds',
            'data-testid': 'bulk-delete-elds-btn',
            label: t.REMOVE_SELECTED,
            onClick: () => setShowBulkDeleteModal(true),
            disabled: selectedElds.length === 0,
          },
        })}
      />
      <Container className={styles.container}>
        <DataGridTable
          title={t.VISIBLE_ELD_PROVIDERS}
          columns={eldListColumns.columns}
          columnVisibilityModel={{id: false}}
          data={eldList}
          loading={loading}
          totalCount={eldList.length}
          multiSearchFilter={false}
          getRowId={(row: any) => row?.id}
          actionList={isViewOnly() ? [] : actionList}
          handleActions={handleActions}
          checkboxSelection={!isViewOnly()}
          onSelectionModelChange={handleSelectionChange}
          sortOrder={'asc'}
          sortField={'eldProviderName'}
        />
      </Container>
      <AvailableElds
        isOpen={availableEldsBladeOpen}
        onClose={() => setAvailableEldsBladeOpen(false)}
        onSuccess={() => {
          setAvailableEldsBladeOpen(false)
          fetchEldList()
        }}
      />
      <SubmitDialog
        cancelId="cancelRemoveEld"
        type="delete"
        open={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        title={t.REMOVE_ELD_PROVIDER}
        body1={selectedRowData?.eldProviderName ? t.REMOVE_ELD_PROVIDER_CONFIRMATION.replace('{0}', selectedRowData.eldProviderName) : ''}
        body2={t.REMOVE_ELD_PROVIDER_WARNING}
        primaryBtnProps={{
          id: "confirmRemoveEld",
          label: t.CONFIRM_REMOVE,
          loading: btnLoader,
          onClick: handleDelete,
          disabled: btnLoader
        }}
      />
      <SubmitDialog
        cancelId="cancelBulkRemoveEld"
        type="delete"
        open={showBulkDeleteModal}
        handleClose={() => setShowBulkDeleteModal(false)}
        title={t.REMOVE_ELD_PROVIDERS}
        body1={t.REMOVE_ELDS_PROVIDER_CONFIRMATION.replace('{0}', selectedElds.length.toString()).replace('{1}', selectedElds.length > 1 ? 's' : '')}
        body2={selectedElds.length > 1 ? t.REMOVE_ELDS_PROVIDER_WARNING : t.REMOVE_ELD_PROVIDER_WARNING}
        primaryBtnProps={{
          id: "confirmBulkRemoveEld",
          label: t.CONFIRM_REMOVE,
          loading: btnLoader,
          onClick: handleDelete,
          disabled: btnLoader
        }}
      />
    </>
  )
}

export default EldVisibilityList 